import { useRef, useState } from 'react'
import { twMerge as twm } from 'tailwind-merge'

import { Icon } from '../../Icon'
import { Spinner } from '../../Spinner'
import Button from '../../app/design/Button'
import Label from '../../app/design/Label'

type Props = {
    title?: string
    subtitle?: string
    buttonTitle?: string
    uploadingState?: 'idle' | 'uploading'
    onSelectFiles?: (files: File[]) => void
    className?: string
    accept?: string[]
}

export const UploadField = ({
    title,
    subtitle,
    buttonTitle = 'Choose file',
    uploadingState,
    onSelectFiles,
    className,
    accept,
}: Props) => {
    const fileInputRef = useRef<HTMLInputElement>(null)

    const [isDraggingOver, setIsDraggingOver] = useState(false)

    const handleDragEvent = (event: React.DragEvent) => {
        if (event.dataTransfer.types.length !== 1 || event.dataTransfer.types[0] !== 'Files') {
            return
        }

        event.preventDefault()
        event.stopPropagation()

        switch (event.type) {
            case 'dragover':
            case 'dragenter': {
                setIsDraggingOver(true)
                break
            }
            case 'dragleave': {
                setIsDraggingOver(false)
                break
            }
        }
    }

    const handleDrop = (event: React.DragEvent) => {
        event.preventDefault()
        event.stopPropagation()

        setIsDraggingOver(false)

        const files = Array.from(event.dataTransfer.files)
        if (files.length === 0) {
            return
        }

        onSelectFiles?.(files)
    }

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files ?? [])
        onSelectFiles?.(files)
    }

    return (
        <div
            className={twm(
                'h-full w-full rounded-lg border border-dashed border-gray-400',
                className
            )}
            onDragOver={handleDragEvent}
            onDragEnter={handleDragEvent}
            onDragLeave={handleDragEvent}
            onDrop={handleDrop}
        >
            {isDraggingOver ? (
                <div className="bg-info-100 flex h-full w-full items-center justify-center">
                    <Label className="text-info-400">Drop files here to upload</Label>
                </div>
            ) : uploadingState === 'uploading' ? (
                <div className="flex h-full w-full flex-col items-center justify-center gap-y-4">
                    <Spinner size={48} />

                    <Label size="md" className="text-gray-700">
                        Uploading...
                    </Label>
                </div>
            ) : (
                <div className="flex h-full w-full flex-col items-center justify-center gap-y-4">
                    <Icon icon="upload.large" background="round" />

                    {(title !== undefined || subtitle !== undefined) && (
                        <div className="flex flex-col items-center justify-center gap-y-1">
                            {title !== undefined && (
                                <Label size="md" className="text-gray-700">
                                    {title}
                                </Label>
                            )}
                            {subtitle !== undefined && (
                                <Label size="sm" className="font-light text-gray-600">
                                    {subtitle}
                                </Label>
                            )}
                        </div>
                    )}

                    <Button
                        intent="secondary"
                        size="small"
                        onClick={() => fileInputRef.current?.click()}
                    >
                        {buttonTitle}
                    </Button>
                </div>
            )}

            <input
                type="file"
                className="hidden"
                multiple
                onChange={handleFileChange}
                ref={fileInputRef}
                accept={accept?.join(',')}
            />
        </div>
    )
}
